import type { PlatformProductVariantSelectFragment } from '~/api/graphql/fragments/platformProduct.generated';
import type { IPriceProductRow, IPriceProductVariantRow } from '../types';

import { PLATFORM_PRODUCT_PRICE_FIELD } from '../../../constants';
import { toVariantPriceRange } from '../../../utils';

export interface IToPriceProductVariantRowsParams {
  /** The product's own baseID, which stands in while its detail is still in flight. */
  baseID: string;
  title: string;
  variants?: PlatformProductVariantSelectFragment[] | null;
}

/**
 * A product always resolves to at least one variant row, so drafts, selection and quick fill only
 * ever address variants and never have to special-case a product. A product whose detail has not
 * landed stands in for a single variant under its own baseID — that keeps a freshly picked row
 * typeable straight away, and the real variants replace it once the fetch returns.
 */
export const toPriceProductVariantRows = ({
  baseID,
  title,
  variants,
}: IToPriceProductVariantRowsParams): IPriceProductVariantRow[] => {
  if (!variants?.length) return [{ baseID, title }];

  return variants.map((variant) => ({
    baseID: variant.baseID,
    title: variant.title ?? '',
    controlPrice: toVariantPriceRange({ variants: [variant] }),
    controlComparePrice: toVariantPriceRange({
      variants: [variant],
      field: PLATFORM_PRODUCT_PRICE_FIELD.COMPARE_PRICE,
    }),
  }));
};

/**
 * Every Control variant across the table, in row order — what select-all and an unfiltered quick fill
 * cover. Named for the Control side specifically: these baseIDs are never the duplicate's.
 */
export const toControlVariantBaseIDs = (rows: IPriceProductRow[]): string[] =>
  rows.flatMap((row) => row.variants.map((variant) => variant.baseID));

/** A product only expands when it stands for more than one variant; the rest have nothing to show. */
export const toExpandableBaseIDs = (rows: IPriceProductRow[]): string[] =>
  rows.filter((row) => row.variantCount > 1).map((row) => row.baseID);
